import { DocumentCard } from '@/components/dashboard/documents-page/DocumentCard'
import ErrorDocumentPage from '@/components/dashboard/documents-page/ErrorDocumentPage'
import { Button } from '@/components/ui/button'
import { getIdTokenUsingQueryClient } from '@/data/queries'
import { createFileRoute, Link, redirect, useRouteContext } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { EmptyHome } from '@/components/dashboard/EmptyHome'
import { LoadingHomePage } from '@/components/loading-ui/LoadingHomePage'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import {
  allDocumentsOptions,
  orgPermissionsCurrentUserOptions,
  userMeProfileOptions,
} from '@/data/queryOptions'
import { REFRESH_INTERVAL } from '@/data/constants'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { DashboardLayout } from '@/routes/_authenticated/_dashboard'
import { filterDocumentsByAccess, getWorkspaceAccess } from '@/data/entities'
import { useMemo } from 'react'

export const Route = createFileRoute('/_authenticated/org/$orgId/home/')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'Home - LineWise',
      },
      {
        name: 'description',
        content: 'Home Page',
      },
    ],
  }),
  loader: async ({ context, params }) => {
    const { authState, queryClient } = context
    const TENANT_ID = params.orgId
    // Sync store with URL param
    const store = useSelectedTenantStore.getState()
    if (TENANT_ID && store.selectedTenantId !== TENANT_ID) {
      store.setSelectedTenantId(TENANT_ID)
    }
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })
    if (!idToken) {
      throw redirect({ to: '/login' })
    }
    return Promise.all([
      queryClient.ensureQueryData(
        allDocumentsOptions({ tenantId: TENANT_ID!, token: idToken! }),
      ),
      queryClient.ensureQueryData(userMeProfileOptions(TENANT_ID, idToken)),
    ])
  },
  pendingComponent: () => <LoadingHomePage />,
  errorComponent: ({ error }) => <ErrorDocumentPage error={error} />,
})

function RouteComponent() {
  const { isOrgAdmin, isOrgEditor } = useRouteContext({
    from: '/_authenticated',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { data: userById } = useSuspenseQuery(
    userMeProfileOptions(TENANT_ID!, idToken!),
  )
  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken),
  )
  const workspaceAccess = useMemo(
    () => getWorkspaceAccess(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const { data: recentDocuments } = useSuspenseQuery({
    ...allDocumentsOptions({ tenantId: TENANT_ID!, token: idToken! }),
    select: (result) =>
      filterDocumentsByAccess(result?.data ?? [], workspaceAccess, {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
      })
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, 3),
    refetchInterval: REFRESH_INTERVAL,
  })
  const { data: latestUpdates } = useSuspenseQuery({
    ...allDocumentsOptions({ tenantId: TENANT_ID!, token: idToken! }),
    select: (result) =>
      filterDocumentsByAccess(result?.data ?? [], workspaceAccess, {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
      })
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, 3),
    refetchInterval: REFRESH_INTERVAL,
  })
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })

  if (recentDocuments.length === 0 && latestUpdates.length === 0) {
    return (
      <DashboardLayout>
        <EmptyHome name={userById?.name ?? 'Unknown'} />
      </DashboardLayout>
    )
  }
  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 px-4 pt-4 sm:gap-12 sm:px-12 sm:pt-12">
        <div className="flex flex-col gap-1">
          <p className="text-base font-semibold sm:text-xl">{currentDate}</p>
          <h1>Hello {userById?.name ?? 'Unknown'}!</h1>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-row items-center justify-between">
            <h3>Recent Documents</h3>
            <Button variant="ghost" asChild>
              <Link to="/org/$orgId/documents" params={{ orgId: TENANT_ID! }}>
                My Documents
                <ChevronRightIcon />
              </Link>
            </Button>
          </div>
          <div className="grid w-full grid-cols-1 grid-rows-3 gap-4 pb-2 lg:grid-cols-3 lg:grid-rows-1">
            {recentDocuments.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                className="col-span-1 row-span-1"
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <h3>Latest Updates</h3>
          <div className="grid w-full grid-cols-1 grid-rows-3 gap-4 pb-2 lg:grid-cols-3 lg:grid-rows-1">
            {latestUpdates.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                className="col-span-1 row-span-1"
              />
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
